import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

import {
  useGetCatalogEducationLevelsQuery,
  useGetCatalogStandardSetsQuery,
  useGetCatalogSubjectsQuery,
  useGetCatalogSetTreeQuery,
} from '../standardsCatalogApi';
import { CatalogSetTree } from '../components/catalog/CatalogSetTree';
import { CatalogSearchResults } from '../components/catalog/CatalogSearchResults';
import { DeprecatedToggleChip, SearchFilterChips } from '../components/catalog/SearchFilterChips';
import { SourceCards, type PickedSource } from '../components/catalog/SourceCards';
import { StandardSetCards } from '../components/catalog/StandardSetCards';
import { EmptyState, SkeletonRows } from '../components/atoms';
import { useCatalogCart } from '../hooks/useCatalogCart';
import { useRecentSources } from '../hooks/useRecentSources';
import { formatEducationLevel, sortEducationLevels } from '../utils/educationLevels';
import { previousStep, type FunnelStep } from '../utils/funnelSteps';

import type { CspStandardSet } from '@/shared/types/userStandard';

interface OpenSet {
  id: string;
  title: string;
}

const PANEL_SX = {
  height: { xs: 'auto', md: 'calc(100vh - 240px)' },
  minHeight: 520,
} as const;

const Centered = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
    {children}
  </Box>
);

export default function FindStandardsView() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [step, setStep] = useState<FunnelStep>('source');
  const [source, setSource] = useState<PickedSource | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  const [grade, setGrade] = useState<string | null>(null);
  const [openSet, setOpenSet] = useState<OpenSet | null>(null);
  const [skips, setSkips] = useState({ subject: false, grade: false });
  const [includeDeprecated, setIncludeDeprecated] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const { items, selectedIds, toggleNode, selectWholeSet } = useCatalogCart();
  const { recordSource } = useRecentSources();

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => clearTimeout(handle);
  }, [query]);

  // Briefly highlight a browsed-to search result, then let it fade.
  useEffect(() => {
    if (!highlightId) return;
    const handle = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(handle);
  }, [highlightId]);

  const searching = Boolean(debouncedQuery);

  const { data: subjects = [], isFetching: subjectsFetching } = useGetCatalogSubjectsQuery(
    { jurisdictionId: source?.id ?? '', includeDeprecated },
    { skip: !source }
  );
  const { data: educationLevels = [], isFetching: levelsFetching } =
    useGetCatalogEducationLevelsQuery(
      { jurisdictionId: source?.id ?? '', subject: subject ?? '', includeDeprecated },
      { skip: !source || !subject }
    );
  const { data: standardSets = [], isFetching: setsFetching } = useGetCatalogStandardSetsQuery(
    {
      jurisdictionId: source?.id ?? '',
      subject: subject ?? '',
      educationLevel: grade,
      includeDeprecated,
    },
    { skip: !source || !subject }
  );
  const { data: setTree = [], isFetching: treeFetching } = useGetCatalogSetTreeQuery(
    { standardSetId: openSet?.id ?? '' },
    { skip: !openSet || searching }
  );

  // Auto-skip the Subject step when a source has exactly one subject.
  useEffect(() => {
    if (step !== 'subject' || !source || subjectsFetching) return;
    if (subjects.length === 1) {
      setSubject(subjects[0]);
      setSkips((s) => ({ ...s, subject: true }));
      setStep('grade');
    }
  }, [step, source, subjectsFetching, subjects]);

  // Auto-skip the Grade step when a subject has zero or one education level.
  useEffect(() => {
    if (step !== 'grade' || !source || !subject || levelsFetching) return;
    if (educationLevels.length <= 1) {
      setSkips((s) => ({ ...s, grade: true }));
      setStep('sets');
    }
  }, [step, source, subject, levelsFetching, educationLevels]);

  const sortedLevels = sortEducationLevels(educationLevels);

  const handlePickSource = (next: PickedSource) => {
    recordSource({ id: next.id, title: next.title });
    setSource(next);
    setSubject(null);
    setGrade(null);
    setOpenSet(null);
    setSkips({ subject: false, grade: false });
    setStep('subject');
  };

  const handlePickSubject = (next: string) => {
    setSubject(next);
    setGrade(null);
    setOpenSet(null);
    setStep('grade');
  };

  const handlePickGrade = (next: string | null) => {
    setGrade(next);
    setOpenSet(null);
    setStep('sets');
  };

  const handlePickSet = (set: CspStandardSet) => {
    setOpenSet({ id: set.id, title: set.title });
    setStep('setTree');
  };

  const goToStep = (target: FunnelStep) => {
    if (target === 'source') {
      setSource(null);
      setSubject(null);
      setGrade(null);
      setOpenSet(null);
      setSkips({ subject: false, grade: false });
    } else if (target === 'subject') {
      setGrade(null);
      setOpenSet(null);
    } else if (target === 'grade') {
      setOpenSet(null);
    } else if (target === 'sets') {
      setOpenSet(null);
    }
    setStep(target);
  };

  const goBack = () => goToStep(previousStep(step, skips));

  const clearSearch = () => {
    setQuery('');
    setDebouncedQuery('');
  };

  // Search-result navigation (Decision 10a): breadcrumb parts and source suggestions jump out of
  // search into the matching browse context.
  const handlePickSourceFromSearch = (next: PickedSource) => {
    clearSearch();
    handlePickSource(next);
  };

  const handlePickSubjectFromResult = (node: {
    jurisdiction_id: string;
    jurisdiction_title: string;
    jurisdiction_type: string | null;
    subject: string;
  }) => {
    clearSearch();
    const picked: PickedSource = {
      id: node.jurisdiction_id,
      title: node.jurisdiction_title,
      type: node.jurisdiction_type,
    };
    recordSource({ id: picked.id, title: picked.title });
    setSource(picked);
    setSubject(node.subject);
    setGrade(null);
    setOpenSet(null);
    setSkips({ subject: false, grade: false });
    setStep('grade');
  };

  const handleBrowseToSet = (node: {
    id: string;
    jurisdiction_id: string;
    jurisdiction_title: string;
    jurisdiction_type: string | null;
    subject: string;
    standard_set_id: string;
    standard_set_title: string;
  }) => {
    clearSearch();
    const picked: PickedSource = {
      id: node.jurisdiction_id,
      title: node.jurisdiction_title,
      type: node.jurisdiction_type,
    };
    recordSource({ id: picked.id, title: picked.title });
    setSource(picked);
    setSubject(node.subject);
    setGrade(null);
    setOpenSet({ id: node.standard_set_id, title: node.standard_set_title });
    setSkips({ subject: false, grade: false });
    setStep('setTree');
    setHighlightId(node.id);
  };

  // Filter chips (Decision 7a): clear one funnel filter while staying in search (keeps the query).
  const clearSourceFilter = () => goToStep('source');
  const clearSubjectFilter = () => {
    setSubject(null);
    setGrade(null);
    setOpenSet(null);
    setSkips((s) => ({ ...s, grade: false }));
    setStep('subject');
  };
  const clearGradeFilter = () => {
    setGrade(null);
    setOpenSet(null);
    setStep('sets');
  };
  const clearSetFilter = () => {
    setOpenSet(null);
    setStep('sets');
  };

  const crumbsRef = useRef<HTMLDivElement>(null);

  const crumbs: { label: string; step: FunnelStep; onClick: () => void }[] = [];
  if (source) {
    crumbs.push({
      label: source.title,
      step: 'subject',
      onClick: () => {
        setSubject(null);
        setGrade(null);
        setOpenSet(null);
        setSkips({ subject: false, grade: false });
        setStep('subject');
      },
    });
  }
  if (subject) {
    crumbs.push({
      label: subject,
      step: 'grade',
      onClick: () => {
        setGrade(null);
        setOpenSet(null);
        setSkips((s) => ({ ...s, grade: false }));
        setStep('grade');
      },
    });
  }
  if (grade) {
    crumbs.push({
      label: formatEducationLevel(grade),
      step: 'sets',
      onClick: () => {
        setOpenSet(null);
        setStep('sets');
      },
    });
  }
  if (openSet) {
    crumbs.push({ label: openSet.title, step: 'setTree', onClick: () => setStep('setTree') });
  }

  // Keep the deepest (rightmost) crumb in view as the trail grows; the user scrolls back to earlier ones.
  const crumbTrail = crumbs.map((crumb) => crumb.label).join(' › ');
  useLayoutEffect(() => {
    const el = crumbsRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [crumbTrail]);

  const renderStep = () => {
    switch (step) {
      case 'source':
        return <SourceCards onPickSource={handlePickSource} />;
      case 'subject':
        if (subjectsFetching)
          return (
            <Box sx={{ p: 2 }}>
              <SkeletonRows count={5} />
            </Box>
          );
        if (subjects.length === 0)
          return (
            <EmptyState
              title="No subjects"
              description="This source has no published standard sets."
            />
          );
        return (
          <Box sx={{ p: 2.5, overflow: 'auto', height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              Choose a subject
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5, maxWidth: 560 }}>
              {subjects.map((s) => (
                <ListItemButton
                  key={s}
                  onClick={() => handlePickSubject(s)}
                  sx={{ borderRadius: 1 }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {s}
                  </Typography>
                  <ChevronRightIcon color="action" />
                </ListItemButton>
              ))}
            </Stack>
          </Box>
        );
      case 'grade':
        if (levelsFetching)
          return (
            <Box sx={{ p: 2 }}>
              <SkeletonRows count={4} />
            </Box>
          );
        return (
          <Box sx={{ p: 2.5, overflow: 'auto', height: '100%' }}>
            <Typography variant="overline" color="text.secondary">
              Choose a grade level
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5, maxWidth: 560 }}>
              <ListItemButton onClick={() => handlePickGrade(null)} sx={{ borderRadius: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  All grade levels
                </Typography>
                <ChevronRightIcon color="action" />
              </ListItemButton>
              {sortedLevels.map((level) => (
                <ListItemButton
                  key={level}
                  onClick={() => handlePickGrade(level)}
                  sx={{ borderRadius: 1 }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {formatEducationLevel(level)}
                  </Typography>
                  <ChevronRightIcon color="action" />
                </ListItemButton>
              ))}
            </Stack>
          </Box>
        );
      case 'sets':
        if (setsFetching)
          return (
            <Box sx={{ p: 2 }}>
              <SkeletonRows count={4} />
            </Box>
          );
        if (standardSets.length === 0)
          return (
            <EmptyState
              title="No standard sets"
              description="No standard sets match this source, subject, and grade level."
            />
          );
        return <StandardSetCards sets={standardSets} onPick={handlePickSet} />;
      case 'setTree':
        if (treeFetching)
          return (
            <Centered>
              <CircularProgress />
            </Centered>
          );
        if (setTree.length === 0)
          return (
            <EmptyState
              title="Empty standard set"
              description="This set has no published standards."
            />
          );
        return (
          <CatalogSetTree
            nodes={setTree}
            items={items}
            onToggle={toggleNode}
            onSelectAll={() => openSet && selectWholeSet(openSet.id, setTree)}
            highlightId={highlightId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', ...PANEL_SX }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search standards, codes, or keywords"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQuery('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {!searching && step !== 'source' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={goBack}
              sx={{ flexShrink: 0 }}
            >
              Back
            </Button>
            <Box
              ref={crumbsRef}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                flexWrap: 'nowrap',
                overflowX: 'auto',
                // Crumbs are reachable by horizontal scroll on small screens; hide the scrollbar.
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {crumbs.map((crumb, index) => (
                <Box
                  key={`${crumb.label}-${index}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
                >
                  {index > 0 && (
                    <Typography variant="subtitle2" color="text.disabled" component="span">
                      ›
                    </Typography>
                  )}
                  {crumb.step === step ? (
                    <Typography
                      variant="subtitle2"
                      color="text.primary"
                      sx={{
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                        maxWidth: { xs: 160, sm: 'none' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {crumb.label}
                    </Typography>
                  ) : (
                    <Link
                      component="button"
                      type="button"
                      variant="subtitle2"
                      color="text.secondary"
                      underline="hover"
                      onClick={crumb.onClick}
                      sx={{
                        whiteSpace: 'nowrap',
                        maxWidth: { xs: 120, sm: 'none' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {crumb.label}
                    </Link>
                  )}
                </Box>
              ))}
            </Box>
            <Box sx={{ ml: 'auto', pl: 1 }}>
              <DeprecatedToggleChip
                active={includeDeprecated}
                onToggle={() => setIncludeDeprecated((v) => !v)}
              />
            </Box>
          </Box>
        )}
        {searching && (
          <SearchFilterChips
            source={source}
            subject={subject}
            grade={grade}
            openSet={openSet}
            includeDeprecated={includeDeprecated}
            onClearSource={clearSourceFilter}
            onClearSubject={clearSubjectFilter}
            onClearGrade={clearGradeFilter}
            onClearSet={clearSetFilter}
            onToggleDeprecated={() => setIncludeDeprecated((v) => !v)}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {searching ? (
          <CatalogSearchResults
            query={debouncedQuery}
            jurisdictionId={source?.id ?? null}
            subject={subject}
            educationLevel={grade}
            standardSetId={openSet?.id ?? null}
            includeDeprecated={includeDeprecated}
            items={items}
            selectedIds={selectedIds}
            onToggle={toggleNode}
            onPickSource={handlePickSourceFromSearch}
            onPickSubject={handlePickSubjectFromResult}
            onBrowseToSet={handleBrowseToSet}
          />
        ) : (
          renderStep()
        )}
      </Box>
    </Paper>
  );
}
